<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card-title class="d-flex justify-space-between align-center flex-nowrap">
          <div>
            <div class="text-h4 font-weight-bold mb-2">Tours</div>
            <div class="subtitle-2 grey--text">Manage your tours.</div>
          </div>
          <div>
            <v-btn color="success" dark small flat @click="onAddTour">
              <v-icon left size="18">mdi-plus</v-icon>
              Create New
            </v-btn>
          </div>
        </v-card-title>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col v-for="tour in tours" :key="tour.id" cols="12" md="6">
        <v-card elevation="4" class="site-card" style="height: 500px">
          <v-col class="card-details pa-4 d-flex flex-column" cols="5" >
            <div class="text-h5 font-weight-bold mb-10">
              {{ tour.name }}
            </div>
            <div class="caption grey--text">Created on</div>
            <div class="subtitle-1 font-weight-bold mb-5">
              {{ formatDate(tour.createdUtc) }}
            </div>
            <div class="caption grey--text">Updated on</div>
            <div class="subtitle-1 font-weight-bold mb-5">
              {{ formatDate(tour.updatedUtc) }}
            </div>
            <div class="caption grey--text">Type</div>
            <div class="subtitle-1 font-weight-bold mb-5">
              {{ tour.tourType }}
            </div>
            <div class="caption grey--text">IsPublished</div>
            <div class="subtitle-1 font-weight-bold mb-5">
              {{ tour.isPublished }}
            </div>
            <div class="caption grey--text">Access (starts)</div>
            <div class="subtitle-1 font-weight-bold mb-5 access-list">
              <span v-if="getAccessList(tour).length">
                <span
                  v-for="(item, idx) in getAccessList(tour)"
                  :key="`${tour.id || tour.Id}-lang-${item.languageCode}-${idx}`"
                >
                  {{ formatAccessItem(item) }}<span v-if="idx < getAccessList(tour).length - 1"> • </span>
                </span>
              </span>
              <span v-else class="grey--text">No access data yet</span>
            </div>
            <v-spacer />
            <div class="card-actions">
              <v-btn
                color="primary"
                class="card-actions__primary"
                small
                @click="onViewDetails(tour.id)"
              >
                View Details →
              </v-btn>
              <v-btn
                outlined
                small
                color="primary"
                class="card-actions__secondary"
                @click="onViewAccess(tour)"
              >
                Access
              </v-btn>
              <v-btn
                outlined
                small
                color="secondary"
                class="card-actions__secondary"
                :loading="Boolean(duplicateBusyByTour[tour.id ?? tour.Id])"
                @click="onDuplicateTour(tour)"
              >
                Copy
              </v-btn>
              <v-btn
                outlined
                small
                color="error"
                class="card-actions__secondary"
                @click="onDeleteTour(tour)"
              >
                Delete
              </v-btn>
            </div>
          </v-col>

          <v-col cols="7" class="card-map pa-0 d-flex">
            <img
              v-if="tour.coverImage"
              class="coverImage"
              :src="tour.coverImage"
              :alt="tour.Name"
            />
            <div v-else class="coverPlaceholder">Image here when available.</div>
          </v-col>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showAccessDialog" max-width="1080">
      <v-card class="access-dialog">
        <div class="access-dialog__hero">
          <div>
            <div class="access-dialog__eyebrow">Tour analytics</div>
            <div class="access-dialog__title">
              {{ accessDialogTour?.name || accessDialogTour?.Name || "Tour" }}
            </div>
            <div class="access-dialog__subtitle">
              Download activity by language, event type, and device session.
            </div>
          </div>
          <v-btn icon class="access-dialog__close" @click="showAccessDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="access-dialog__body">
          <div v-if="accessSummaryList.length" class="access-overview">
            <div class="access-metric-grid">
              <div
                v-for="metric in accessOverviewMetrics"
                :key="metric.label"
                class="access-metric-card"
              >
                <div class="access-metric-card__label">{{ metric.label }}</div>
                <div class="access-metric-card__value">{{ metric.value }}</div>
              </div>
            </div>

            <div class="access-language-section">
              <div class="access-section-heading">Totals by language</div>
              <div class="access-language-grid">
                <div
                  v-for="item in accessSummaryList"
                  :key="`access-${item.languageCode}`"
                  class="access-language-card"
                >
                  <div class="access-language-card__header">
                    <div class="access-language-card__code">
                      {{ (item.languageCode || "unknown").toUpperCase() }}
                    </div>
                    <div class="access-language-card__name">
                      {{ item.languageName || "Unknown language" }}
                    </div>
                  </div>
                  <div class="access-language-card__stats">
                    <div class="access-language-stat">
                      <span class="access-language-stat__label">Starts</span>
                      <span class="access-language-stat__value">{{ item.starts }}</span>
                    </div>
                    <div class="access-language-stat">
                      <span class="access-language-stat__label">Completes</span>
                      <span class="access-language-stat__value">{{ item.completes }}</span>
                    </div>
                    <div class="access-language-stat">
                      <span class="access-language-stat__label">Fails</span>
                      <span class="access-language-stat__value">{{ item.fails }}</span>
                    </div>
                    <div class="access-language-stat">
                      <span class="access-language-stat__label">Total</span>
                      <span class="access-language-stat__value">{{ item.total }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="access-empty-state">
            <div class="access-section-heading">No access data yet</div>
            <div class="access-dialog__subtitle">
              Once visitors start downloading this tour, the language and event breakdown will appear here.
            </div>
          </div>

          <div class="access-events-shell">
            <div class="access-events-shell__top">
              <div>
                <div class="access-section-heading">Download times</div>
                <div class="access-events-shell__subtitle">
                  Start and completion time for each visitor download.
                </div>
              </div>
              <div class="access-events-shell__count">
                {{ downloadAttempts.length }} attempts
              </div>
            </div>

            <div v-if="accessDialogLoading" class="access-feedback grey--text">
              Loading downloads...
            </div>
            <div v-else-if="!downloadAttempts.length" class="access-feedback grey--text">
              No download attempts recorded yet.
            </div>
            <div v-else class="access-table-wrap">
              <table class="access-table">
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>1_InitialLoad</th>
                    <th>2_TourDownload</th>
                    <th>Status</th>
                    <th>Lang</th>
                    <th>Device</th>
                    <th>OS</th>
                    <th>Connection</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(attempt, idx) in downloadAttempts" :key="`attempt-${idx}`">
                    <td class="access-table__time">
                      {{ formatAccessTimestamp(attempt.startedUtc) }}
                    </td>
                    <td>{{ formatInitialLoad(attempt) }}</td>
                    <td>{{ formatDownloadDuration(attempt.durationSeconds) }}</td>
                    <td>
                      <v-chip
                        x-small
                        label
                        :color="attempt.completedUtc ? 'success' : 'warning'"
                        text-color="white"
                      >
                        {{ attempt.completedUtc ? "Complete" : "Not completed" }}
                      </v-chip>
                    </td>
                    <td>
                      <span class="access-lang-badge">
                        {{ (attempt.languageCode || "unknown").toUpperCase() }}
                      </span>
                    </td>
                    <td>{{ attempt.deviceType || "Unknown" }}</td>
                    <td>{{ attempt.operatingSystem || "Unknown" }}</td>
                    <td>{{ formatConnection(attempt) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="access-events-shell">
            <div class="access-events-shell__top">
              <div>
                <div class="access-section-heading">Recent events</div>
                <div class="access-events-shell__subtitle">
                  Filter the latest access activity by language or event type.
                </div>
              </div>
              <div class="access-events-shell__count">{{ accessEvents.length }} events</div>
            </div>

            <v-row dense class="access-filter-row">
              <v-col cols="12" md="6">
                <v-select
                  v-model="accessFilterLanguage"
                  :items="accessLanguageOptions"
                  item-text="text"
                  item-value="value"
                  label="Language"
                  dense
                  outlined
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="accessFilterEventType"
                  :items="accessEventTypeOptions"
                  item-text="text"
                  item-value="value"
                  label="Event type"
                  dense
                  outlined
                  hide-details
                />
              </v-col>
            </v-row>

            <div v-if="accessDialogLoading" class="access-feedback grey--text">Loading events...</div>
            <div v-else-if="accessDialogError" class="access-feedback error--text">{{ accessDialogError }}</div>
            <div v-else-if="!accessEvents.length" class="access-feedback grey--text">
              No events match the current filters.
            </div>
            <div v-else class="access-table-wrap">
              <table class="access-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Event</th>
                    <th>Lang</th>
                    <th>Client</th>
                    <th>Version</th>
                    <th>Agent</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(evt, idx) in accessEvents" :key="`evt-${idx}`">
                    <td class="access-table__time">{{ formatAccessTimestamp(evt.createdUtc) }}</td>
                    <td>
                      <v-chip
                        x-small
                        label
                        :color="accessEventChipColor(evt.eventType)"
                        text-color="white"
                      >
                        {{ formatEventType(evt.eventType) }}
                      </v-chip>
                    </td>
                    <td>
                      <span class="access-lang-badge">
                        {{ (evt.languageCode || "unknown").toUpperCase() }}
                      </span>
                    </td>
                    <td class="mono access-table__client">{{ evt.clientId }}</td>
                    <td>{{ evt.appVersion || "N/A" }}</td>
                    <td class="access-agent" :title="evt.userAgent || 'N/A'">
                      {{ formatUserAgent(evt.userAgent) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showCreateDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">Create Tour</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newTourName"
            label="Tour name"
            outlined
            dense
            :disabled="createBusy"
          />
          <v-text-field
            v-model="newTourSlug"
            label="Slug (optional)"
            outlined
            dense
            hint="Leave blank to auto-generate from the name."
            persistent-hint
            :disabled="createBusy"
          />
          <div v-if="createError" class="error--text caption">{{ createError }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeCreateDialog" :disabled="createBusy">Cancel</v-btn>
          <v-btn color="success" dark @click="submitCreateTour" :loading="createBusy">
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  fetchTours,
  createTour,
  duplicateTour,
  deleteTour,
  getTourAccessSummary,
  getTourAccessEvents,
  getTourDownloadAttempts,
} from "@/api/tours";

export default {
  components: {},
  props: {
    id: { type: Number, required: true }, // siteId
  },
  data() {
    return {
      tours: [],
      showCreateDialog: false,
      newTourName: "",
      newTourSlug: "",
      createBusy: false,
      createError: "",
      duplicateBusyByTour: {},
      accessSummaryByTour: {},
      showAccessDialog: false,
      accessDialogTour: null,
      accessDialogLoading: false,
      accessDialogError: "",
      accessEvents: [],
      downloadAttempts: [],
      accessFilterLanguage: null,
      accessFilterEventType: null,
      accessEventTypeOptions: [
        { text: "All events", value: null },
        { text: "Download start", value: "download_start" },
        { text: "Download complete", value: "download_complete" },
        { text: "Download failed", value: "download_failed" },
      ],
    };
  },

  computed: {
    accessSummaryList() {
      const tour = this.accessDialogTour;
      if (!tour) return [];
      const tourId = tour.id ?? tour.Id;
      return this.accessSummaryByTour[tourId] || [];
    },
    accessLanguageOptions() {
      const base = [{ text: "All languages", value: null }];
      const list = this.accessSummaryList;
      return base.concat(
        list.map((item) => {
          const code = (item.languageCode || "").toUpperCase();
          const name = item.languageName ? ` - ${item.languageName}` : "";
          return { text: `${code}${name}`.trim(), value: item.languageCode };
        }),
      );
    },
    accessOverviewMetrics() {
      const totals = this.accessSummaryList.reduce(
        (acc, item) => {
          acc.starts += item.starts ?? 0;
          acc.completes += item.completes ?? 0;
          acc.fails += item.fails ?? 0;
          acc.total += item.total ?? 0;
          return acc;
        },
        { starts: 0, completes: 0, fails: 0, total: 0 },
      );

      return [
        { label: "Languages", value: this.accessSummaryList.length },
        { label: "Starts", value: totals.starts },
        { label: "Completes", value: totals.completes },
        { label: "Fails", value: totals.fails },
        { label: "Events logged", value: totals.total },
      ];
    },
  },

  mounted() {
    this.loadTours();
  },
  methods: {
    async loadTours() {
      try {
        const toursData = await fetchTours(this.id);
        this.tours = toursData;
        this.loadAccessSummary();
      } catch (err) {
        console.error("Error fetching tours:", err);
      }
    },
    async loadAccessSummary() {
      const siteId = this.$route.params.id ?? this.id;
      if (!siteId) return;
      try {
        const summary = await getTourAccessSummary(siteId);
        this.accessSummaryByTour = (summary || []).reduce((acc, item) => {
          acc[item.tourId] = item.languages || [];
          return acc;
        }, {});
      } catch (err) {
        console.error("Error fetching tour access summary:", err);
        this.accessSummaryByTour = {};
      }
    },
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
    getAccessList(tour) {
      const tourId = tour?.id ?? tour?.Id;
      if (!tourId) return [];
      return this.accessSummaryByTour[tourId] || [];
    },
    formatAccessItem(item) {
      if (!item) return "";
      const code = (item.languageCode || "unknown").toUpperCase();
      const count = item.starts ?? 0;
      return `${code} ${count}`;
    },
    onAddTour() {
      this.showCreateDialog = true;
    },
    closeCreateDialog() {
      this.showCreateDialog = false;
      this.newTourName = "";
      this.newTourSlug = "";
      this.createError = "";
    },
    async submitCreateTour() {
      const name = (this.newTourName || "").trim();
      if (!name) {
        this.createError = "Tour name is required.";
        return;
      }

      this.createBusy = true;
      this.createError = "";

      try {
        const siteId = this.$route.params.id ?? this.id;
        const created = await createTour({
          siteId: Number(siteId),
          name,
          slug: (this.newTourSlug || "").trim() || null,
        });
        this.tours = [created, ...this.tours];
        this.closeCreateDialog();
      } catch (err) {
        console.error("Error creating tour:", err);
        this.createError = "Create failed. Please try again.";
      } finally {
        this.createBusy = false;
      }
    },
    onViewDetails(tourId) {
      if (!tourId) {
        console.warn("onViewDetails called without a tourId");
        return;
      }
      // siteId comes from this page's route/prop
      const siteId = this.$route.params.id ?? this.id; // whichever you use
      this.$router.push({
        name: "TourEditor",
        params: {
          siteId: String(siteId),
          tourId: String(tourId),
        },
      });
    },
    async onDeleteTour(tour) {
      const tourId = tour?.id ?? tour?.Id;
      if (!tourId) {
        console.warn("onDeleteTour called without a tourId");
        return;
      }
      if (!confirm(`Delete "${tour.name || tour.Name || "this tour"}"?`)) return;

      try {
        await deleteTour(tourId);
        this.tours = this.tours.filter((t) => (t.id ?? t.Id) !== tourId);
      } catch (err) {
        console.error("Error deleting tour:", err);
      }
    },
    async onDuplicateTour(tour) {
      const tourId = tour?.id ?? tour?.Id;
      if (!tourId) return;

      const name = tour?.name || tour?.Name || "this tour";
      if (!confirm(`Create a full copy of "${name}"? This will duplicate steps and media files.`)) {
        return;
      }

      this.duplicateBusyByTour = {
        ...this.duplicateBusyByTour,
        [tourId]: true,
      };

      try {
        const created = await duplicateTour(tourId);
        this.tours = [created, ...this.tours];
      } catch (err) {
        console.error("Error duplicating tour:", err);
      } finally {
        const { [tourId]: _removed, ...rest } = this.duplicateBusyByTour;
        this.duplicateBusyByTour = rest;
      }
    },
    async onViewAccess(tour) {
      this.accessDialogTour = tour;
      this.accessFilterLanguage = null;
      this.accessFilterEventType = null;
      this.accessEvents = [];
      this.downloadAttempts = [];
      this.accessDialogError = "";
      this.showAccessDialog = true;
      await this.loadAccessEvents();
    },
    async loadAccessEvents() {
      const tourId = this.accessDialogTour?.id ?? this.accessDialogTour?.Id;
      if (!tourId) return;
      this.accessDialogLoading = true;
      this.accessDialogError = "";
      try {
        const [events, attempts] = await Promise.all([
          getTourAccessEvents({
            tourId,
            languageCode: this.accessFilterLanguage,
            eventType: this.accessFilterEventType,
            take: 50,
          }),
          getTourDownloadAttempts({ tourId, take: 100 }),
        ]);
        this.accessEvents = events || [];
        this.downloadAttempts = attempts || [];
      } catch (err) {
        console.error("Error fetching access events:", err);
        this.accessDialogError = "Failed to load access events.";
        this.accessEvents = [];
        this.downloadAttempts = [];
      } finally {
        this.accessDialogLoading = false;
      }
    },
    formatAccessTimestamp(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }).replace(",", " ·");
    },
    formatEventType(value) {
      if (!value) return "Unknown";
      return value
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    },
    formatDownloadDuration(value) {
      if (value === null || value === undefined) return "—";
      const seconds = Math.max(0, Math.round(Number(value)));
      if (seconds < 60) return `${seconds}s`;
      const minutes = Math.floor(seconds / 60);
      const remainder = seconds % 60;
      return `${minutes}m ${remainder}s`;
    },
    formatInitialLoad(attempt) {
      const duration = this.formatDownloadDuration(
        attempt?.initialLoadDurationSeconds,
      );
      if (duration === "—") return duration;
      return attempt?.initialLoadNavigationType
        ? `${duration} (${attempt.initialLoadNavigationType})`
        : duration;
    },
    formatConnection(attempt) {
      const details = [];
      if (attempt?.connectionType) {
        details.push(String(attempt.connectionType).toUpperCase());
      }
      if (attempt?.downlinkMbps !== null && attempt?.downlinkMbps !== undefined) {
        details.push(`${Number(attempt.downlinkMbps).toFixed(1)} Mbps`);
      }
      if (attempt?.rttMs !== null && attempt?.rttMs !== undefined) {
        details.push(`${attempt.rttMs} ms RTT`);
      }
      return details.length ? details.join(" · ") : "Unavailable";
    },
    accessEventChipColor(value) {
      switch (value) {
        case "download_complete":
          return "success";
        case "download_failed":
          return "error";
        case "download_start":
          return "primary";
        default:
          return "grey";
      }
    },
    formatUserAgent(value) {
      if (!value) return "N/A";
      if (value.includes("Android")) return "Android device";
      if (value.includes("iPhone")) return "iPhone";
      if (value.includes("iPad")) return "iPad";
      if (value.includes("Windows")) return "Windows desktop";
      if (value.includes("Mac OS X")) return "Mac desktop";
      return value;
    },
  },
  watch: {
    accessFilterLanguage() {
      if (this.showAccessDialog) this.loadAccessEvents();
    },
    accessFilterEventType() {
      if (this.showAccessDialog) this.loadAccessEvents();
    },
  },
};
</script>
<style scoped>
.site-card {
  display: flex;
  width: 100%;
  height: 400px;
  border-radius: 8px;
  overflow: hidden;
}
.card-details {
  flex: 0 0 41.6667%;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
}
.card-map {
  flex: 1 1 0;
  position: relative;
  display: flex;
  overflow: hidden;
  z-index: 0;
  padding: 0 !important;
  margin-left: auto;
}
.card-actions {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  width: 100%;
}
.card-actions .v-btn {
  margin: 0;
  min-width: 0;
}
.card-actions__primary {
  grid-column: 1 / -1;
}
.card-actions__secondary {
  width: 100%;
}
.card-map .map-container {
  width: 100% !important;
  height: 100% !important;
  border-radius: 0;
}
.text-h4 {
  font-size: 1.5rem;
  line-height: 2rem;
}
.font-weight-bold {
  font-weight: 700 !important;
}
.font-weight-medium {
  font-weight: 500 !important;
}
.caption {
  font-size: 0.75rem;
  line-height: 1rem;
}
.grey--text {
  color: #757575 !important;
}
.subtitle-1 {
  font-size: 1rem;
  line-height: 1.5rem;
  color: #212121;
}
.access-list {
  line-height: 1.3rem;
}
.mono {
  font-family: Consolas, "Courier New", monospace;
  font-size: 0.75rem;
}
.access-agent {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.body-2 {
  width: 200px;
  font-size: 0.9rem;
  line-height: 1.25rem;
}
.subtitle-2 {
  font-size: 0.875rem;
}
.access-dialog {
  border-radius: 24px;
  overflow: hidden;
}
.access-dialog__hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 24px 28px 20px;
  background: linear-gradient(135deg, #f6f8fc 0%, #eef3fb 100%);
  border-bottom: 1px solid #dfe6f1;
}
.access-dialog__eyebrow {
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #5c6f91;
  margin-bottom: 6px;
}
.access-dialog__title {
  font-size: 1.65rem;
  line-height: 1.2;
  font-weight: 700;
  color: #1f2a3d;
}
.access-dialog__subtitle {
  margin-top: 6px;
  color: #607089;
  font-size: 0.95rem;
}
.access-dialog__close {
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 10px 24px rgba(52, 76, 122, 0.12);
}
.access-dialog__body {
  padding: 24px 28px 28px;
  background: #f8fafc;
}
.access-overview {
  display: grid;
  gap: 20px;
}
.access-metric-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 14px;
}
.access-metric-card {
  padding: 16px 18px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e2e8f3;
  box-shadow: 0 12px 28px rgba(29, 51, 84, 0.06);
}
.access-metric-card__label {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6f7f95;
  margin-bottom: 8px;
}
.access-metric-card__value {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1c2740;
}
.access-section-heading {
  font-size: 1rem;
  font-weight: 700;
  color: #22304a;
  margin-bottom: 12px;
}
.access-language-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
}
.access-language-card {
  padding: 16px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid #e2e8f3;
  box-shadow: 0 12px 24px rgba(29, 51, 84, 0.05);
}
.access-language-card__header {
  margin-bottom: 14px;
}
.access-language-card__code {
  font-size: 1rem;
  font-weight: 700;
  color: #234277;
}
.access-language-card__name {
  margin-top: 2px;
  font-size: 0.85rem;
  color: #67778d;
}
.access-language-card__stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 14px;
}
.access-language-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.access-language-stat__label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #7c8aa0;
}
.access-language-stat__value {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2a3d;
}
.access-empty-state {
  padding: 18px 20px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px dashed #ced8e6;
}
.access-events-shell {
  margin-top: 24px;
  padding: 20px;
  border-radius: 22px;
  background: #ffffff;
  border: 1px solid #e2e8f3;
  box-shadow: 0 16px 30px rgba(29, 51, 84, 0.06);
}
.access-events-shell__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}
.access-events-shell__subtitle {
  color: #66778f;
  font-size: 0.9rem;
}
.access-events-shell__count {
  padding: 8px 12px;
  border-radius: 999px;
  background: #eef4ff;
  color: #29539b;
  font-size: 0.82rem;
  font-weight: 700;
  white-space: nowrap;
}
.access-filter-row {
  margin-bottom: 12px;
}
.access-feedback {
  padding: 14px 4px 4px;
}
.access-table-wrap {
  overflow: auto;
  border: 1px solid #e7edf6;
  border-radius: 18px;
}
.access-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 860px;
  background: #fff;
}
.access-table thead th {
  position: sticky;
  top: 0;
  background: #f5f8fd;
  color: #5f6f85;
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  text-align: left;
  padding: 12px 14px;
  border-bottom: 1px solid #e1e8f2;
}
.access-table tbody td {
  padding: 12px 14px;
  border-bottom: 1px solid #edf2f8;
  vertical-align: top;
  color: #263247;
}
.access-table tbody tr:hover {
  background: #f8fbff;
}
.access-table__time {
  min-width: 210px;
  white-space: nowrap;
}
.access-table__client {
  font-size: 0.7rem;
  color: #516173;
  min-width: 250px;
  white-space: nowrap;
}
.access-lang-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  padding: 5px 10px;
  border-radius: 999px;
  background: #eef4ff;
  color: #21498d;
  font-size: 0.76rem;
  font-weight: 700;
}

.coverImage{
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
.coverPlaceholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #757575;
  font-size: 0.9rem;
  background: #f5f5f5;
}

@media (max-width: 1100px) {
  .card-actions {
    gap: 8px;
  }
  .card-actions .v-btn {
    padding: 4px 10px;
    font-size: 12px;
  }
  .access-metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .access-dialog__hero,
  .access-dialog__body {
    padding-left: 18px;
    padding-right: 18px;
  }
  .access-events-shell__top {
    flex-direction: column;
  }
  .access-metric-grid {
    grid-template-columns: 1fr;
  }
}
</style>
